<template>
  <b-modal v-model="visible" lazy no-close-on-backdrop no-close-on-esc hide-footer hide-header @hide="finished">

    <b-alert v-if="err" show variant="danger">
      <pre>{{ err }}</pre>
    </b-alert>

    <loading-spinner class="loading-panel" v-else-if="loading"/>

    <template v-else-if="Array.isArray(referrals)">
      <template v-for="({ id: referralID, job, contact, shareCode }) in referrals">
        <share v-if="referralID && job && contact && shareCode" :key="referralID" :title="`Share this role with ${contact.fullName}`"
          :url="absoluteShareUrl" show-preview :preview-args="{ title: job.title }"
          @modal-close="() => promptOnClose ? promptRemove() : finished()" @track="({ channel }) => trackShareClick({ channel, shareCode })">
            <template v-slot:text>
              <p>Click one of the buttons below to share with {{contact.fullName}} &amp; we'll let you know when they apply.</p>
            </template>
        </share>
      </template>
    </template>

  </b-modal>
</template>

<script>
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import Share from '@/components/Share';
import ContactReferralsFragment from '@/graphql/fragments/ContactReferrals.gql';
import ReferralsCreateMutation from '@/graphql/mutations/ReferralsCreate.gql';
import ReferralsDeleteMutation from '@/graphql/mutations/ReferralsDelete.gql';
import { mapRoot } from '@src/mixins/root';

export default {
  name: 'EmployeeMakesReferralModal',
  props: {
    jobID: {
      type: String,
      required: true,
    },
    selectedContactIDs: {
      type: Array,
      required: true,
    },
    eventID: {
      type: String,
      default: null,
    },
  },
  components: {
    LoadingSpinner,
    Share,
  },
  data: () => ({
    loading: false,
    visible: false,
    err: null,
    referrals: null,
    sharedBy: null,
  }),
  computed: {
    ...mapRoot([ 'user', 'origin', 'isSainsburys' ]),
    referralID() {
      if (Array.isArray(this.referrals) && this.referrals.length === 1 && this.referrals[0] && this.referrals[0].id) {
        return this.referrals[0].id;
      } else {
        return null;
      }
    },
    absoluteShareUrl() {
      const { eventID, origin, shareCode } = this;
      const path = eventID ? `/e/r/${eventID}/${shareCode}` : `/r/${shareCode}`;
      return `${origin}${path}`;
    },
    promptOnClose() {
      if (Array.isArray(this.referrals) && this.referrals.length === 1 && this.referrals[0] && this.referrals[0].id) {
        const { created, updated } = this.referrals[0];
        return created === updated && !this.sharedBy;
      }

      return false;
    },
  },
  methods: {
    async show() {
      try {
        this.err = null;
        this.loading = true;
        this.sharedBy = null;
        this.$assert(this.user && this.user.id, new Error('You are not logged-in?'));

        this.$log.debug({ contactIDs: this.selectedContactIDs });

        const create = this.selectedContactIDs.map(contactID => ({
          job: { id: this.jobID },
          contact: { id: contactID },
          user: { id: this.user.id },
          status: 'ACCEPTED',
        }));

        this.$log.debug({ create });

        const { data: { createManyReferrals: referrals } } = await this.$apollo.mutate({
          mutation: ReferralsCreateMutation,
          variables: { create },
        });

        this.referrals = referrals;
        this.loading = false;
        this.visible = true;
      } catch (err) {
        this.err = `${err}`;
      }
    },
    trackShareClick({ channel, shareCode }) {
      Promise.resolve(this.$root.captureLog({
        eventName: 'REFERRAL_SHARED',
        job: { id: this.jobID },
        user: { self: true },
        referral: { share: shareCode },
        ...(this.eventID ? { event: { id: this.eventID } } : null),
        ...(channel ? { data: [ { key: 'channel', value: channel } ] } : null),
      }));
      this.sharedBy = channel || true;
      this.$emit('trackShareClick', { channel });
    },
    async promptRemove(close) {
      const text = 'You haven’t shared the role yet, are you sure you want to cancel?';
      const confirmed = await this.$bvModal.msgBoxConfirm(text, {
        buttonSize: 'sm',
        okTitle: 'Yes',
        okVariant: 'danger',
        cancelTitle: 'No',
        cancelVariant: 'outline-success',
        footerClass: 'p-2',
      });

      if (confirmed) {
        const { referralID } = this;
        const [ contactID ] = this.selectedContactIDs;

        if (referralID) {
          await this.$apollo.mutate({
            mutation: ReferralsDeleteMutation,
            variables: { where: { id: referralID } },
            update(store) {
              if (contactID) {
                const id = `Contact:${contactID}`;
                const data = store.readFragment({ fragment: ContactReferralsFragment, id });
                if (data && Array.isArray(data.referrals)) {
                  const i = data.referrals.findIndex(r => r && r.id === referralID);
                  if (i >= 0) {
                    data.referrals.splice(i, 1);
                  }
                  store.writeFragment({ fragment: ContactReferralsFragment, id, data });
                }
              }
            },
          });
        }

        this.finished();
      }
    },
    finished() {
      this.loading = false;
      this.referrals = null;
      this.visible = false;
      this.$emit('clearBasket');
    },
  },
};
</script>

<style lang="scss">
@import './../scss/variables';
.employee-makes-referral-modal {
  .btn {
    display: block;
    margin: 0 auto;
    margin-top: 1rem;
    margin-bottom: 1rem;
    &.refer {
      width: 13rem;
      margin: inherit;
      margin-top: 0.5rem;
      border-radius: 0;
      margin-left: auto;
    }
    &.finished {
      width: 16rem;
      margin: 0 auto;
      border-radius: 0;
    }
  }

  .card-body {
    padding: 0;
    padding-top: 1rem;
    .footer-buttons {
      position: relative;
      text-align: center;
      padding: 0;

      .request-buttons {
        display: flex;
        flex-direction: row;
        border-top-style: solid;
        border-width: 1px;
        border-color: #dadada;
        .request-button {
          padding: 5px;
          padding-top: 10px;
          padding-bottom: 10px;
          flex: 1;
          &:hover {
            opacity: 0.6;
            text-decoration: underline;
            cursor: pointer;
          }
        }
        > :first-child {
          background-color: white;
          border-right-style: solid;
          border-right-width: 1px;
          border-color: #dadada;
          .round-icon {
            background-color: rgba 229 52 59, 0.15;
            color: #e5343b;
          }
        }
        > :nth-child(2) {
          background-color: white;
          border-color: #dadada;
          .round-icon {
            background-color: rgba 255 186 0, 0.15;
          }
        }
        > :nth-child(3) {
          color: white;
          font-weight: bolder;
          &:hover {
            opacity: 0.85;
          }
          .round-icon {
            // background-color: white;
            // color: green;
          }
        }
      }
    }
  }
  .content {
    margin-bottom: 1rem;
    padding: 1rem 2rem 1rem 2rem;
    h3 {
      margin-bottom: 1em;
    }
    .card {
      margin-top: 1em;
      p {
        margin: 1rem;
        // font-size: 0.8rem;
      }

      .card-body {
        padding: 0;
        margin: 0;
        img {
          margin-top: 1rem;
          margin-bottom: 0.4rem;
          height: 60px;
          width: 60px;
        }

        .jobTitle {
          margin-bottom: 1rem;
        }
        text-align: center;
      }
    }

    &.no-referral {
      .form-group{
        label {
          font-weight: normal;
        }
        padding-left: 4rem;
        padding-right: 4rem;
        text-align: left;
        input {
          padding: 0.375rem 0.75rem;
          font-size: 0.8rem;
          margin-top: 0.6rem;
        }
      }
    }
    &.refer {
      text-align: center;
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/modals/employee-makes-referral.vue